import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';
import { Check, Sparkles, Loader2, BadgeCheck, Brain, Trophy, MessagesSquare, Target, Shield, Zap, X as XIcon } from 'lucide-react';

// ─── Plan catalogue (frontend-visible only) ─────────────────────────
// Pricing/features are duplicated from the backend for layout reasons.
// The backend (`PLANS`) remains the source of truth for billing; this
// list only drives what the card shows.
const PLANS = [
  {
    id: 'starter',
    nameKey: 'plan_starter',
    price: '$14.99',
    periodKey: 'month',
    accent: false,
    features: [
      'feat_playbook_full',
      'feat_advanced_editor',
      'feat_unlimited_roster',
      'feat_50_ai',
      'feat_unlimited_invites',
    ],
    addOnHintKey: 'plan_starter_addons_hint',
  },
  {
    id: 'pro_coach',
    nameKey: 'plan_pro_coach',
    price: '$29.99',
    periodKey: 'month',
    accent: true,
    badgeKey: 'plan_most_popular',
    features: [
      'feat_everything_starter',
      'feat_150_ai',
      'feat_individual_stats',
      'feat_didactic_photos',
      'feat_global_plays',
      'feat_30_players',
      'feat_includes_iq_score',
    ],
  },
  {
    id: 'premium_annual',
    nameKey: 'plan_premium_annual',
    price: '$249.99',
    periodKey: 'year',
    accent: false,
    badgeKey: 'plan_best_value',
    features: [
      'feat_everything_unlimited',
      'feat_multi_coach',
      'feat_academy_mode',
      'feat_personal_branding',
      'feat_priority_support',
      'feat_includes_iq_full_suite',
    ],
  },
];

// Add-ons available to Starter users (Pro Coach already bundles IQ
// Score; Premium bundles everything). The backend will still validate
// that an add-on actually unlocks something for this user before
// creating the Stripe session.
const ADDONS = [
  { id: 'addon_iq_score',      featureKey: 'addon_iq_score_label',      descKey: 'addon_iq_score_desc',      price: '$4.99', icon: Trophy },
  { id: 'addon_iq_simulator',  featureKey: 'addon_iq_simulator_label',  descKey: 'addon_iq_simulator_desc',  price: '$4.99', icon: Brain },
  { id: 'addon_ai_coach',      featureKey: 'addon_ai_coach_label',      descKey: 'addon_ai_coach_desc',      price: '$6.99', icon: MessagesSquare },
  { id: 'addon_shot_analyzer', featureKey: 'addon_shot_analyzer_label', descKey: 'addon_shot_analyzer_desc', price: '$6.99', icon: Target },
];

export default function Pricing() {
  const { t } = useLang();
  const { search } = useLocation();
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [checkoutId, setCheckoutId] = useState(null);

  useEffect(() => {
    if (new URLSearchParams(search).get('cancelled') === '1') {
      toast.error(t('pay_cancelled'));
    }
  }, [search, t]);

  useEffect(() => {
    let mounted = true;
    api.get('/payments/me')
      .then(({ data }) => { if (mounted) setPlan(data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoadingPlan(false); });
    return () => { mounted = false; };
  }, []);

  const subscribe = async (planId) => {
    setCheckoutId(planId);
    try {
      const { data } = await api.post('/payments/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else { toast.error(t('pay_error_title')); setCheckoutId(null); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      setCheckoutId(null);
    }
  };

  const buyAddon = async (addonId) => {
    setCheckoutId(addonId);
    try {
      const { data } = await api.post('/payments/addon/checkout', {
        addon_id: addonId,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else { toast.error(t('pay_error_title')); setCheckoutId(null); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      setCheckoutId(null);
    }
  };

  const activePlan = plan?.active ? plan.plan : null;
  const activeAddons = plan?.addons || {};
  const expiresLabel = plan?.plan_expires_at
    ? new Date(plan.plan_expires_at).toLocaleDateString()
    : null;

  // Helpers — derive feature ownership for the addon row badges.
  const features = plan?.features || {};
  const featureToAddon = {
    addon_iq_score: 'iq_score',
    addon_iq_simulator: 'iq_simulator',
    addon_ai_coach: 'ai_coach',
    addon_shot_analyzer: 'shot_analyzer',
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="mb-10 text-center relative">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_pricing')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('pricing_h1')}</h1>
        <p className="text-gray-400 mt-2 max-w-xl mx-auto">{t('pricing_subtitle')}</p>

        {/* Trust strip */}
        <div className="mt-5 flex items-center justify-center gap-4 flex-wrap text-[11px] font-mono uppercase tracking-widest text-slate-400">
          <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> Pagos Stripe</span>
          <span className="text-white/20">·</span>
          <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-400" /> Cancelás cuando quieras</span>
          <span className="text-white/20">·</span>
          <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-gold" /> Setup en 60 segundos</span>
        </div>

        {activePlan && (
          <div
            data-testid="active-plan-banner"
            className="mt-5 inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold rounded-full px-4 py-1.5 text-xs font-mono uppercase tracking-widest"
          >
            <BadgeCheck className="w-4 h-4" />
            {t('pay_current_plan')}: {t(PLANS.find(p => p.id === activePlan)?.nameKey || activePlan) || activePlan}
            {expiresLabel && <span className="text-gold/70 normal-case tracking-normal ml-1">· {t('pay_expires_on')} {expiresLabel}</span>}
          </div>
        )}
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto items-stretch">
        {PLANS.map(p => {
          const isActive = activePlan === p.id;
          const isLoading = checkoutId === p.id;
          // Compute monthly-equivalent for annual plan to make value obvious.
          const monthlyEq = p.id === 'premium_annual' ? '~$20.83' : null;
          const monthlySaving = p.id === 'premium_annual' ? 'Ahorrás $109.89/año vs Pro mensual' : null;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl p-7 flex flex-col transition-transform hover:-translate-y-1 ${
                p.accent
                  ? 'bg-gradient-to-br from-gold/15 via-card-steel to-card-steel border-2 border-gold shadow-[0_0_40px_-12px_rgba(212,175,55,0.6)] md:scale-[1.03]'
                  : 'bg-card-steel border border-gold/25'
              } ${isActive ? 'ring-2 ring-gold ring-offset-2 ring-offset-navy' : ''}`}
            >
              {p.badgeKey && !isActive && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-h uppercase tracking-widest px-3 py-1 rounded-full ${
                  p.accent ? 'bg-gold text-navy' : 'bg-purple-500 text-white'
                }`}>
                  {t(p.badgeKey)}
                </div>
              )}
              {isActive && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-xs font-h uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3" /> {t('pay_active')}
                </div>
              )}
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t(p.nameKey)}</div>
              <div className="flex items-end gap-1 mb-1">
                <div className="font-h text-5xl text-white">{p.price}</div>
                <div className="text-sm text-gray-400 mb-1.5">/ {t(p.periodKey || 'month')}</div>
              </div>
              {monthlyEq && (
                <div className="mb-3 text-[11px] text-emerald-300 font-mono">
                  ≈ {monthlyEq}/mes · <span className="text-emerald-200">{monthlySaving}</span>
                </div>
              )}
              {!monthlyEq && <div className="mb-3 h-[14px]" />}
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((fk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span>{t(fk)}</span>
                  </li>
                ))}
              </ul>
              {p.addOnHintKey && (
                <div className="text-[11px] text-gray-400 italic mb-3 leading-snug">
                  + {t(p.addOnHintKey)}
                </div>
              )}
              <button
                data-testid={`subscribe-${p.id}`}
                onClick={() => subscribe(p.id)}
                disabled={loadingPlan || isLoading || isActive}
                className={`w-full px-5 py-3 rounded-md font-h uppercase tracking-wider font-semibold transition flex items-center justify-center gap-2 ${
                  p.accent
                    ? 'bg-gold text-navy hover:bg-gold-light disabled:opacity-60'
                    : 'border-2 border-gold/60 text-gold hover:bg-gold/10 disabled:opacity-60'
                }`}
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isActive ? t('pay_active') : isLoading ? t('pay_redirecting') : t('subscribe')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Add-ons row */}
      <div className="mt-12 max-w-6xl mx-auto">
        <div className="text-center mb-5">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('addons_eyebrow')}</div>
          <h2 className="font-h text-2xl sm:text-3xl text-white">{t('addons_title')}</h2>
          <p className="text-sm text-gray-400 mt-1">{t('addons_subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ADDONS.map((a) => {
            const Icon = a.icon;
            const featureKey = featureToAddon[a.id];
            const hasFeature = features[featureKey];
            const ownedByAddon = !!activeAddons[featureKey];
            const isLoading = checkoutId === a.id;
            return (
              <div
                key={a.id}
                data-testid={`addon-card-${a.id}`}
                className={`relative bg-card-steel border rounded-lg p-5 flex flex-col ${ownedByAddon ? 'border-gold/60' : 'border-gold/20'}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-h text-white">{a.price}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">/ {t('month')}</div>
                  </div>
                </div>
                <div className="font-h text-base text-white mb-1">{t(a.featureKey)}</div>
                <div className="text-xs text-gray-400 leading-snug flex-1">{t(a.descKey)}</div>
                <button
                  data-testid={`buy-addon-${a.id}`}
                  onClick={() => buyAddon(a.id)}
                  disabled={loadingPlan || isLoading || (hasFeature && !ownedByAddon)}
                  className="mt-4 w-full px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {ownedByAddon
                    ? t('addon_renew')
                    : hasFeature
                    ? t('addon_included')
                    : isLoading
                    ? t('pay_redirecting')
                    : t('addon_buy')}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-12 max-w-6xl mx-auto" data-testid="pricing-compare">
        <div className="text-center mb-5">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">Comparativa</div>
          <h2 className="font-h text-2xl sm:text-3xl text-white">Elegí el plan que te va</h2>
        </div>
        <div className="overflow-x-auto rounded-xl border border-gold/20 bg-card-steel">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-white/10">
                <th className="px-4 py-3">Feature</th>
                <th className="px-4 py-3 text-center">Starter</th>
                <th className="px-4 py-3 text-center text-gold">Pro Coach</th>
                <th className="px-4 py-3 text-center text-purple-300">Premium Annual</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {[
                ['Playbook completo + editor', true, true, true],
                ['Roster ilimitado', true, true, true],
                ['Invitaciones jugadores', true, true, true],
                ['IA Coach mensajes', '50/mes', '150/mes', 'Ilimitado'],
                ['IQ Score (live game)', false, true, true],
                ['Game IQ Simulator', false, false, true],
                ['Shot Analyzer', false, false, true],
                ['Multi-equipo (academia)', false, false, true],
                ['Multi-coach', false, false, true],
                ['Personal branding', false, false, true],
                ['Soporte prioritario', false, false, true],
              ].map(([label, s, pc, pa], i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2.5">{label}</td>
                  <td className="px-4 py-2.5 text-center">{s === true ? <Check className="w-4 h-4 inline text-gold" /> : s === false ? <XIcon className="w-4 h-4 inline text-slate-600" /> : <span className="text-slate-300">{s}</span>}</td>
                  <td className="px-4 py-2.5 text-center bg-gold/5">{pc === true ? <Check className="w-4 h-4 inline text-gold" /> : pc === false ? <XIcon className="w-4 h-4 inline text-slate-600" /> : <span className="text-gold">{pc}</span>}</td>
                  <td className="px-4 py-2.5 text-center bg-purple-500/5">{pa === true ? <Check className="w-4 h-4 inline text-purple-300" /> : pa === false ? <XIcon className="w-4 h-4 inline text-slate-600" /> : <span className="text-purple-200">{pa}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10 text-center text-xs text-gray-500">
        <Sparkles className="w-3 h-3 inline mr-1 text-gold" />
        {t('payments_secure')}
      </div>
    </Layout>
  );
}
